import { ReactNode } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LogOut, FileText, Newspaper, Image as ImageIcon, Users, Home } from "lucide-react";

const links = [
  { to: "/admin", label: "Etusivu", icon: Home, end: true },
  { to: "/admin/sivut", label: "Sivut", icon: FileText },
  { to: "/admin/blogi", label: "Blogi", icon: Newspaper },
  { to: "/admin/kuvat", label: "Kuvat", icon: ImageIcon },
  { to: "/admin/kayttajat", label: "Käyttäjät", icon: Users },
];

const AdminLayout = ({ children }: { children?: ReactNode }) => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Ladataan...</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <h1 className="text-2xl font-bold">Ei käyttöoikeutta</h1>
        <p className="text-muted-foreground">Tilisi ei ole admin-roolissa.</p>
        <Button onClick={async () => { await signOut(); navigate("/admin/login"); }}>
          Kirjaudu ulos
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <h1 className="font-display text-lg font-semibold">Hallintapaneeli</h1>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">{user.email}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => { await signOut(); navigate("/admin/login"); }}
            >
              <LogOut className="mr-2 h-4 w-4" /> Kirjaudu ulos
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl flex-wrap gap-1 px-4 pb-2">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">{children ?? <Outlet />}</main>
    </div>
  );
};

export default AdminLayout;
